import React, { useCallback, useEffect, useState } from 'react';
import { useApiCaller } from '../../utils/ApiUtilities';
import { useError } from '../ErrorContext';
import {
    getUserSearchUrl,
    getAwardsUrl,
    getAwardPostUrl,
    getAwardPutUrl,
    getAwardEmailUrl,
    getAwardFinalizeUrl,
} from '../../utils/UrlUtilities';
import {
    constructAwardPayload,
    constructAwardEmailPayload,
    constructAwardCommentPayload,
    constructAwardFinalizePayload,
} from '../../utils/PayloadUtils';
import { QUIZ_TYPE_LABELS, TAGLEVELS, itemsPerPageOptions } from '../../utils/Constants';
import { isEmpty } from '../../utils/BasicUtilities';

const CATEGORY_OPTIONS = [
    { value: 'general', label: 'General' },
    ...Object.entries(QUIZ_TYPE_LABELS).map(([value, label]) => ({ value, label })),
];

// Flatten TAGLEVELS (some entries have submenus) into {value,label} options.
const LEVEL_OPTIONS = TAGLEVELS.flatMap((item) =>
    item.submenu
        ? item.submenu.map((s) => ({ value: s.value, label: `${item.label} — ${s.label}` }))
        : [{ value: item.value, label: item.label }]
);

const STATUS_BADGE = { SUCCESS: 'bg-success', PENDING: 'bg-warning text-dark', FAILED: 'bg-danger' };

export default function AwardManagement() {
    const CallApi = useApiCaller();
    const { showSuccess } = useError();

    // Two tabs: "send" (create + list general awards) and "referral" (finalize drafted
    // referral cash rewards). The grid filters by award_type to match the active tab.
    const [activeTab, setActiveTab] = useState('send');
    // ── Send panel ──
    const [userQuery, setUserQuery] = useState('');
    const [userResults, setUserResults] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [form, setForm] = useState({ phone: '', amount: '', category: 'general', level: '', reason: '' });
    const [sending, setSending] = useState(false);
    // Recipient email composed inline (auto-generated, editable) + a confirm gate.
    const [emailEnabled, setEmailEnabled] = useState(true);
    const [emailSubject, setEmailSubject] = useState('');
    const [emailBody, setEmailBody] = useState('');
    const [emailEdited, setEmailEdited] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);

    // ── Grid ──
    const [awards, setAwards] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(50);
    const [loading, setLoading] = useState(false);
    const [pending, setPending] = useState({ q: '', category: '', level: '', status: '', date_from: '', date_to: '' });
    const [applied, setApplied] = useState({ q: '', category: '', level: '', status: '', date_from: '', date_to: '' });

    // ── Modals ──
    const [emailFor, setEmailFor] = useState(null); // award row
    const [emailForm, setEmailForm] = useState({ subject: '', body: '' });
    const [emailSending, setEmailSending] = useState(false);
    const [noteFor, setNoteFor] = useState(null);
    const [noteText, setNoteText] = useState('');
    const [noteSaving, setNoteSaving] = useState(false);

    // Referral-reward finalize (approve + pay) modal.
    const [finalizeFor, setFinalizeFor] = useState(null); // award row
    const [finalizeForm, setFinalizeForm] = useState({ amount: '', phone: '' });
    const [finalizing, setFinalizing] = useState(false);

    // Debounced user search.
    useEffect(() => {
        if (isEmpty(userQuery) || selectedUser) { setUserResults([]); return undefined; }
        let cancelled = false;
        const t = setTimeout(async () => {
            const res = await CallApi({ url: getUserSearchUrl({ q: userQuery, limit: 8 }), method: 'GET', token: true });
            if (!cancelled && res.success) setUserResults(res.data || []);
        }, 300);
        return () => { cancelled = true; clearTimeout(t); };
    }, [userQuery, selectedUser, CallApi]);

    const fetchAwards = useCallback(async () => {
        setLoading(true);
        const params = { page, limit, award_type: activeTab === 'referral' ? 'REFERRAL' : 'GENERAL' };
        Object.entries(applied).forEach(([k, v]) => { if (!isEmpty(v)) params[k] = v; });
        const res = await CallApi({ url: getAwardsUrl(params), method: 'GET', token: true });
        if (res.success) {
            setAwards(res.data.awards || []);
            setTotal(res.data.total || 0);
        }
        setLoading(false);
    }, [page, limit, applied, activeTab, CallApi]);

    useEffect(() => { fetchAwards(); }, [fetchAwards]);

    // Auto-generate the recipient email from amount/phone/reason until the admin edits it.
    useEffect(() => {
        if (!selectedUser || !emailEnabled || emailEdited) return;
        const amt = form.amount || 0;
        setEmailSubject(`You've received a ৳${amt} award from Kobra Prep`);
        setEmailBody(
            `Hi ${selectedUser.name || 'there'},\n\n`
            + `Congratulations! You've been awarded ৳${amt}`
            + (form.reason ? ` for ${form.reason}` : '')
            + `. It will be sent to your bKash number ${form.phone || ''}.\n\n`
            + `Thank you for being with Kobra Prep.\n— Kobra Prep Team`
        );
    }, [selectedUser, form.amount, form.phone, form.reason, emailEnabled, emailEdited]);

    const pickUser = (u) => {
        setSelectedUser(u);
        setUserResults([]);
        setUserQuery(`${u.name} (${u.email})`);
        setForm((f) => ({ ...f, phone: u.phone || '' }));
    };
    const clearUser = () => {
        setSelectedUser(null);
        setUserQuery('');
        setForm((f) => ({ ...f, phone: '' }));
        setEmailEnabled(true);
        setEmailEdited(false);
        setEmailSubject('');
        setEmailBody('');
    };

    const handleSend = async () => {
        if (!selectedUser || !(Number(form.amount) > 0)) return;
        setConfirmOpen(false);
        setSending(true);
        const res = await CallApi({
            url: getAwardPostUrl(),
            method: 'POST',
            payload: constructAwardPayload({
                userId: selectedUser.id, recipientPhone: form.phone, amountBdt: form.amount,
                category: form.category, level: form.level, reason: form.reason,
                sendEmail: emailEnabled, emailSubject, emailBody,
            }),
            token: true,
        });
        setSending(false);
        if (res.success) {
            const emailNote = emailEnabled
                ? (res.data?.last_email_status === 'SENT' ? ' Email sent.' : ' Email could not be sent.')
                : '';
            showSuccess((res.data?.status === 'SUCCESS' ? 'Award sent.' : `Award recorded (${res.data?.status}).`) + emailNote);
            clearUser();
            setForm({ phone: '', amount: '', category: 'general', level: '', reason: '' });
            fetchAwards();
        }
    };

    const handleSendEmail = async () => {
        if (isEmpty(emailForm.subject)) return;
        setEmailSending(true);
        const res = await CallApi({
            url: getAwardEmailUrl(emailFor.award_id),
            method: 'POST',
            payload: constructAwardEmailPayload(emailForm.subject, emailForm.body),
            token: true,
        });
        setEmailSending(false);
        if (res.success) {
            showSuccess(res.data?.status === 'SENT' ? 'Email sent.' : `Email failed: ${res.data?.error || ''}`);
            setEmailFor(null);
            setEmailForm({ subject: '', body: '' });
            fetchAwards();
        }
    };

    const handleSaveNote = async () => {
        setNoteSaving(true);
        const res = await CallApi({
            url: getAwardPutUrl(noteFor.award_id),
            method: 'PUT',
            payload: constructAwardCommentPayload(noteText),
            token: true,
        });
        setNoteSaving(false);
        if (res.success) { showSuccess('Note saved.'); setNoteFor(null); fetchAwards(); }
    };

    const handleFinalize = async () => {
        if (!finalizeFor) return;
        setFinalizing(true);
        const res = await CallApi({
            url: getAwardFinalizeUrl(finalizeFor.award_id),
            method: 'POST',
            payload: constructAwardFinalizePayload({ amountBdt: finalizeForm.amount, recipientPhone: finalizeForm.phone }),
            token: true,
        });
        setFinalizing(false);
        if (res.success) {
            showSuccess(res.data?.status === 'SUCCESS' ? 'Referral reward paid.' : `Reward ${res.data?.status}.`);
            setFinalizeFor(null);
            setFinalizeForm({ amount: '', phone: '' });
            fetchAwards();
        }
    };

    const totalPages = Math.ceil(total / limit) || 1;

    return (
        <div className="container py-4">
            <h2 className="fw-bold mb-3" style={{ color: '#4c1d95' }}>Award Management</h2>

            {/* Tabs */}
            <ul className="nav nav-tabs mb-4">
                <li className="nav-item">
                    <button className={`nav-link ${activeTab === 'send' ? 'active' : ''}`} onClick={() => { setActiveTab('send'); setPage(1); }}>
                        Send an Award
                    </button>
                </li>
                <li className="nav-item">
                    <button className={`nav-link ${activeTab === 'referral' ? 'active' : ''}`} onClick={() => { setActiveTab('referral'); setPage(1); }}>
                        Referral Rewards
                    </button>
                </li>
            </ul>

            {activeTab === 'referral' && (
                <div className="alert alert-info py-2 small">
                    These cash rewards were auto-drafted when a referred friend bought their first plan.
                    Review each one and click <strong>Finalize &amp; Pay</strong> to send the bKash payout.
                </div>
            )}

            {/* ── Send Award (send tab only) ── */}
            {activeTab === 'send' && (
            <div className="card mb-4 kp-filter">
                <div className="card-header fw-bold" style={{ background: 'linear-gradient(145deg,#7c3aed,#5b21b6)', color: '#fff' }}>
                    Send an Award
                </div>
                <div className="card-body">
                    <label className="form-label small fw-semibold">Find recipient (name / email / phone)</label>
                    <div style={{ position: 'relative' }}>
                        <input
                            className="form-control"
                            placeholder="Search a user…"
                            value={userQuery}
                            onChange={(e) => { setUserQuery(e.target.value); setSelectedUser(null); }}
                        />
                        {userResults.length > 0 && (
                            <ul className="list-group position-absolute w-100" style={{ zIndex: 20, maxHeight: 260, overflowY: 'auto' }}>
                                {userResults.map((u) => (
                                    <li key={u.id} className="list-group-item list-group-item-action" role="button" onClick={() => pickUser(u)}>
                                        <div className="fw-semibold">{u.name}</div>
                                        <div className="small text-muted">{u.email}{u.phone ? ` · ${u.phone}` : ''}</div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    {selectedUser && (
                        <div className="border rounded p-3 mt-3 bg-light">
                            <div className="d-flex justify-content-between align-items-start">
                                <div>
                                    <div className="fw-bold">{selectedUser.name}</div>
                                    <div className="small">{selectedUser.email}</div>
                                    <div className="small text-muted">Phone: {selectedUser.phone || '—'}{selectedUser.institute ? ` · ${selectedUser.institute}` : ''}</div>
                                </div>
                                <button className="btn btn-sm btn-outline-secondary" onClick={clearUser}>Change</button>
                            </div>
                            <div className="row g-2 mt-2">
                                <div className="col-12 col-md-4">
                                    <label className="form-label small mb-1">bKash number (payout)</label>
                                    <input className="form-control form-control-sm" value={form.phone} placeholder="+8801XXXXXXXXX" onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                                </div>
                                <div className="col-6 col-md-2">
                                    <label className="form-label small mb-1">Amount (৳)</label>
                                    <input type="number" min="1" className="form-control form-control-sm" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
                                </div>
                                <div className="col-6 col-md-3">
                                    <label className="form-label small mb-1">Category</label>
                                    <select className="form-select form-select-sm" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                                        {CATEGORY_OPTIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                                    </select>
                                </div>
                                <div className="col-6 col-md-3">
                                    <label className="form-label small mb-1">Level</label>
                                    <select className="form-select form-select-sm" value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })}>
                                        <option value="">—</option>
                                        {LEVEL_OPTIONS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
                                    </select>
                                </div>
                                <div className="col-12">
                                    <label className="form-label small mb-1">Reason</label>
                                    <input className="form-control form-control-sm" value={form.reason} placeholder="e.g. Monthly top performer" onChange={(e) => setForm({ ...form, reason: e.target.value })} />
                                </div>
                            </div>

                            {/* Email composer (auto-generated, editable) — configured before sending. */}
                            <div className="form-check mt-3">
                                <input className="form-check-input" type="checkbox" id="awardEmailToggle" checked={emailEnabled} onChange={(e) => setEmailEnabled(e.target.checked)} />
                                <label className="form-check-label small" htmlFor="awardEmailToggle">
                                    Also email <strong>{selectedUser.email || '—'}</strong> from support@kobraprep.com
                                </label>
                            </div>
                            {emailEnabled && (
                                <div className="border rounded p-2 mt-2 bg-white">
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                        <label className="form-label small mb-0 fw-semibold">Email subject</label>
                                        {emailEdited && (
                                            <button type="button" className="btn btn-link btn-sm p-0" onClick={() => setEmailEdited(false)}>Reset to auto</button>
                                        )}
                                    </div>
                                    <input className="form-control form-control-sm mb-2" value={emailSubject} onChange={(e) => { setEmailEdited(true); setEmailSubject(e.target.value); }} />
                                    <label className="form-label small mb-1 fw-semibold">Email message</label>
                                    <textarea className="form-control form-control-sm" rows={5} value={emailBody} onChange={(e) => { setEmailEdited(true); setEmailBody(e.target.value); }} />
                                </div>
                            )}

                            <button className="btn btn-primary mt-3" onClick={() => setConfirmOpen(true)} disabled={sending || !(Number(form.amount) > 0)}>
                                {sending ? 'Sending…' : `Send ৳${form.amount || 0} Award`}
                            </button>
                        </div>
                    )}
                </div>
            </div>
            )}

            {/* ── Filters ── */}
            <div className="card mb-3"><div className="card-body row g-2 align-items-end">
                <div className="col-6 col-md-3">
                    <label className="form-label small mb-1">Search</label>
                    <input className="form-control form-control-sm" value={pending.q} onChange={(e) => setPending({ ...pending, q: e.target.value })} placeholder="name / email / phone" />
                </div>
                <div className="col-6 col-md-2">
                    <label className="form-label small mb-1">Category</label>
                    <select className="form-select form-select-sm" value={pending.category} onChange={(e) => setPending({ ...pending, category: e.target.value })}>
                        <option value="">All</option>
                        {CATEGORY_OPTIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                </div>
                <div className="col-6 col-md-2">
                    <label className="form-label small mb-1">Level</label>
                    <select className="form-select form-select-sm" value={pending.level} onChange={(e) => setPending({ ...pending, level: e.target.value })}>
                        <option value="">All</option>
                        {LEVEL_OPTIONS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
                    </select>
                </div>
                <div className="col-6 col-md-2">
                    <label className="form-label small mb-1">Status</label>
                    <select className="form-select form-select-sm" value={pending.status} onChange={(e) => setPending({ ...pending, status: e.target.value })}>
                        <option value="">All</option>
                        <option value="SUCCESS">Success</option>
                        <option value="PENDING">Pending</option>
                        <option value="FAILED">Failed</option>
                    </select>
                </div>
                <div className="col-6 col-md-2">
                    <label className="form-label small mb-1">From</label>
                    <input type="date" className="form-control form-control-sm" value={pending.date_from} onChange={(e) => setPending({ ...pending, date_from: e.target.value })} />
                </div>
                <div className="col-6 col-md-2">
                    <label className="form-label small mb-1">To</label>
                    <input type="date" className="form-control form-control-sm" value={pending.date_to} onChange={(e) => setPending({ ...pending, date_to: e.target.value })} />
                </div>
                <div className="col-6 col-md-1">
                    <button className="btn btn-sm btn-outline-primary w-100" onClick={() => { setApplied(pending); setPage(1); }}>Filter</button>
                </div>
            </div></div>

            {/* ── Grid ── */}
            <div className="card"><div className="card-body p-0 table-responsive">
                <table className="table table-hover mb-0">
                    <thead className="table-light">
                        <tr>
                            <th>Recipient</th><th>Amount</th>
                            <th>{activeTab === 'referral' ? 'Referred friend' : 'Category'}</th>
                            <th>Level</th><th>Status</th>
                            <th>Email</th><th>Comment</th><th>Date</th><th className="text-end">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={9} className="text-center text-muted py-4">Loading…</td></tr>
                        ) : awards.length === 0 ? (
                            <tr><td colSpan={9} className="text-center text-muted py-4">No awards found.</td></tr>
                        ) : awards.map((a) => (
                            <tr key={a.award_id}>
                                <td>
                                    <div className="fw-semibold">{a.recipient_name || '—'}</div>
                                    <div className="small text-muted">{a.recipient_email}</div>
                                    <div className="small text-muted">{a.recipient_phone || <span className="text-warning">no bKash #</span>}</div>
                                </td>
                                <td>৳{a.amount_bdt}</td>
                                <td className="small">
                                    {activeTab === 'referral'
                                        ? (a.referee_name || a.referee_email || '—')
                                        : (a.category || '—')}
                                </td>
                                <td className="small">{a.level || '—'}</td>
                                <td><span className={`badge ${STATUS_BADGE[a.status] || 'bg-secondary'}`}>{a.status}</span></td>
                                <td className="small">
                                    {a.email_count > 0
                                        ? <span className={a.last_email_status === 'SENT' ? 'text-success' : 'text-danger'}>{a.last_email_status} ({a.email_count})</span>
                                        : <span className="text-muted">—</span>}
                                </td>
                                <td className="small" style={{ maxWidth: 180 }}>{a.admin_comment || a.reason || <span className="text-muted">—</span>}</td>
                                <td className="small text-muted">{new Date(a.created_at).toLocaleDateString()}</td>
                                <td className="text-end">
                                    {a.award_type === 'REFERRAL' && a.status === 'PENDING' && (
                                        <button className="btn btn-sm btn-success me-1" onClick={() => { setFinalizeFor(a); setFinalizeForm({ amount: String(a.amount_bdt || ''), phone: a.recipient_phone || '' }); }}>
                                            Finalize &amp; Pay
                                        </button>
                                    )}
                                    <button className="btn btn-sm btn-outline-primary me-1" onClick={() => { setEmailFor(a); setEmailForm({ subject: '', body: '' }); }}>✉ Email</button>
                                    <button className="btn btn-sm btn-outline-secondary" onClick={() => { setNoteFor(a); setNoteText(a.admin_comment || ''); }}>Note</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div></div>

            {/* ── Pagination ── */}
            <div className="d-flex justify-content-between align-items-center mt-3">
                <div className="d-flex align-items-center gap-2">
                    <label className="small mb-0">Items per page:</label>
                    <select className="form-select form-select-sm w-auto" value={limit} onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}>
                        {itemsPerPageOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                </div>
                <div className="d-flex align-items-center gap-2">
                    <button className="btn btn-sm btn-outline-secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</button>
                    <span className="small text-muted">Page {page} of {totalPages}</span>
                    <button className="btn btn-sm btn-outline-secondary" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</button>
                </div>
            </div>

            {/* ── Confirm send ── */}
            {confirmOpen && selectedUser && (
                <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1060 }}>
                    <div className="card" style={{ width: '90%', maxWidth: 'min(480px, 94vw)' }}>
                        <div className="card-header fw-bold" style={{ background: 'linear-gradient(145deg,#7c3aed,#5b21b6)', color: '#fff' }}>Confirm award</div>
                        <div className="card-body">
                            <p className="mb-2">
                                Are you sure you want to send <strong>৳{form.amount || 0}</strong> to <strong>{selectedUser.name}</strong> &lt;{selectedUser.email}&gt;?
                            </p>
                            <p className="small text-muted mb-1">bKash: {form.phone || '—'}{form.reason ? ` · ${form.reason}` : ''}</p>
                            {emailEnabled
                                ? <p className="small text-muted mb-0">A confirmation email will be sent to {selectedUser.email} from support@kobraprep.com.</p>
                                : <p className="small text-muted mb-0">No email will be sent (only an in-app notification).</p>}
                            <p className="small text-danger mt-2 mb-0">This records a payout and cannot be undone.</p>
                        </div>
                        <div className="card-footer d-flex justify-content-end gap-2">
                            <button className="btn btn-outline-secondary" onClick={() => setConfirmOpen(false)} disabled={sending}>Cancel</button>
                            <button className="btn btn-primary" onClick={handleSend} disabled={sending}>{sending ? 'Sending…' : 'Yes, send'}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Finalize referral reward modal ── */}
            {finalizeFor && (
                <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1060 }}>
                    <div className="card" style={{ width: '90%', maxWidth: 'min(480px, 94vw)' }}>
                        <div className="card-header fw-bold" style={{ background: 'linear-gradient(145deg,#7c3aed,#5b21b6)', color: '#fff' }}>Finalize referral reward</div>
                        <div className="card-body">
                            <p className="small mb-2">
                                Pay <strong>{finalizeFor.recipient_name}</strong> &lt;{finalizeFor.recipient_email}&gt; for referring{' '}
                                <strong>{finalizeFor.referee_name || finalizeFor.referee_email || 'a friend'}</strong>.
                            </p>
                            <div className="row g-2">
                                <div className="col-6">
                                    <label className="form-label small mb-1">Amount (৳)</label>
                                    <input type="number" min="1" className="form-control form-control-sm" value={finalizeForm.amount} onChange={(e) => setFinalizeForm({ ...finalizeForm, amount: e.target.value })} />
                                </div>
                                <div className="col-6">
                                    <label className="form-label small mb-1">bKash number</label>
                                    <input className="form-control form-control-sm" placeholder="+8801XXXXXXXXX" value={finalizeForm.phone} onChange={(e) => setFinalizeForm({ ...finalizeForm, phone: e.target.value })} />
                                </div>
                            </div>
                            <p className="small text-danger mt-2 mb-0">This records a bKash payout and cannot be undone.</p>
                        </div>
                        <div className="card-footer d-flex justify-content-end gap-2">
                            <button className="btn btn-outline-secondary" onClick={() => setFinalizeFor(null)} disabled={finalizing}>Cancel</button>
                            <button className="btn btn-success" onClick={handleFinalize} disabled={finalizing || isEmpty(finalizeForm.phone)}>{finalizing ? 'Paying…' : 'Pay reward'}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Email modal ── */}
            {emailFor && (
                <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050 }}>
                    <div className="card" style={{ width: '90%', maxWidth: 'min(560px, 94vw)', maxHeight: '90vh', overflowY: 'auto' }}>
                        <div className="card-header fw-bold" style={{ background: 'linear-gradient(145deg,#7c3aed,#5b21b6)', color: '#fff' }}>
                            Email {emailFor.recipient_name || emailFor.recipient_email}
                        </div>                        <div className="card-body">
                            <div className="small text-muted mb-2">To: {emailFor.recipient_email} · From: support@kobraprep.com</div>
                            <div className="mb-2">
                                <label className="form-label fw-semibold">Subject</label>
                                <input className="form-control" value={emailForm.subject} onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })} />
                            </div>
                            <div className="mb-2">
                                <label className="form-label fw-semibold">Message</label>
                                <textarea className="form-control" rows={6} value={emailForm.body} onChange={(e) => setEmailForm({ ...emailForm, body: e.target.value })} />
                            </div>
                        </div>
                        <div className="card-footer d-flex justify-content-end gap-2">
                            <button className="btn btn-outline-secondary" onClick={() => setEmailFor(null)}>Cancel</button>
                            <button className="btn btn-primary" onClick={handleSendEmail} disabled={emailSending || isEmpty(emailForm.subject)}>{emailSending ? 'Sending…' : 'Send email'}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Note modal ── */}
            {noteFor && (
                <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050 }}>
                    <div className="card" style={{ width: '90%', maxWidth: 'min(520px, 94vw)' }}>
                        <div className="card-header fw-bold" style={{ background: 'linear-gradient(145deg,#7c3aed,#5b21b6)', color: '#fff' }}>Comment</div>
                        <div className="card-body">
                            <textarea className="form-control" rows={4} value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Private note for future reference…" />
                        </div>
                        <div className="card-footer d-flex justify-content-end gap-2">
                            <button className="btn btn-outline-secondary" onClick={() => setNoteFor(null)}>Cancel</button>
                            <button className="btn btn-primary" onClick={handleSaveNote} disabled={noteSaving}>{noteSaving ? 'Saving…' : 'Save note'}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
